import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ApiClass } from '@data/class/api-class';
import { IResponse } from '@data/interfaces/iresponseArray.interface';

@Injectable({
  providedIn: 'root'
})
export class ApiCustomerBillingService extends ApiClass {
  public id: string = '';
  public page: number = 1;
  public limit: number = 10;
  
  constructor() {
    super();
    this.path = 'billing';
  }

  // Crear billing
  create(data: any): Observable<IResponse> {
    return this.postCollection(data, 'create').pipe(
      map((r) => r as IResponse),
      catchError((error) => {
        console.error('Error al crear billing:', error);
        return of({ error: true, msg: 'Error al crear billing', data: null });
      })
    );
  }

  // Obtener todos los billings con filtros
  getAll(filters?: any): Observable<IResponse> {
    const requestBody = {
      page: this.page,
      limit: this.limit,
      filters: filters || {}
    };
    
    return this.postCollection(requestBody).pipe(
      map((r) => r as IResponse),
      catchError((error) => {
        console.error('Error al obtener billings:', error);
        return of({ error: true, msg: 'Error al obtener billings', data: null });
      })
    );
  }

  // Obtener billing por ID
  getById(id: string): Observable<IResponse> {
    return this.getCollection(id).pipe(
      map((r) => r as IResponse),
      catchError((error) => {
        console.error('Error al obtener billing por ID:', error);
        return of({ error: true, msg: 'Error al obtener billing', data: null });
      })
    );
  }

  // Actualizar billing
  update(id: string, data: any): Observable<IResponse> {
    return this.putCollection(data, undefined, `billing/${id}`).pipe(
      map((r) => r as IResponse),
      catchError((error) => {
        console.error('Error al actualizar billing:', error);
        return of({ error: true, msg: 'Error al actualizar billing', data: null });
      })
    );
  }

  // Actualizar billing parcial
  updatePartial(id: string, data: any): Observable<IResponse> {
    return this.patchCollection(data, undefined, `billing/${id}`).pipe(
      map((r) => r as IResponse),
      catchError((error) => {
        console.error('Error al actualizar billing parcialmente:', error);
        return of({ error: true, msg: 'Error al actualizar billing', data: null });
      })
    );
  }

  // Eliminar billing
  delete(id: string): Observable<IResponse> {
    return this.deleteCollection(id).pipe(
      map((r) => r as IResponse),
      catchError((error) => {
        console.error('Error al eliminar billing:', error);
        return of({ error: true, msg: 'Error al eliminar billing', data: null });
      })
    );
  }

  // Método específico para obtener billings de un customer
  getByCustomerId(customerId: string, filters?: any): Observable<IResponse> {
    const requestBody = {
      page: this.page,
      limit: this.limit,
      filters: {
        ...filters,
        customerId: {
          value: customerId,
          operator: 'eq'
        }
      }
    };
    
    return this.postCollection(requestBody).pipe(
      map((r) => r as IResponse),
      catchError((error) => {
        console.error('Error al obtener billings por customer:', error);
        return of({ error: true, msg: 'Error al obtener billings del cliente', data: null });
      })
    );
  }

  // Método específico para crear billing asociado a un lease
  createWithLease(data: any, leaseId: string): Observable<IResponse> {
    const billingData = {
      ...data,
      leaseId: leaseId
    };
    
    return this.postCollection(billingData, 'create').pipe(
      map((r) => r as IResponse),
      catchError((error) => {
        console.error('Error al crear billing con lease:', error);
        return of({ error: true, msg: 'Error al crear billing con lease', data: null });
      })
    );
  }

  // Establecer parámetros de paginación
  setPagination(page: number, limit: number): void {
    this.page = page;
    this.limit = limit;
  }

  // Establecer ID para operaciones específicas
  setId(id: string): void {
    this.id = id;
  }
}